import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/news", label: "影视资讯" },
  { to: "/actors", label: "演员介绍" },
  { to: "/about", label: "关于我们" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
      isActive
        ? "text-primary-600 font-semibold"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="hidden md:block bg-primary-900 text-background-200">
        <div className="px-4 md:px-8 lg:px-12 h-9 flex items-center justify-between text-xs">
          <span className="flex items-center gap-2">
            <i className="ri-movie-2-line" />
            欢迎来到漂亮的保姆 · 影视剧资讯与剧情介绍
          </span>
          <span className="flex items-center gap-2 text-background-300">
            <i className="ri-information-line" />
            演示站点 · 内容为前端模拟数据
          </span>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12">
        <div className="h-16 flex items-center gap-4 md:gap-8">
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <span className="w-9 h-9 rounded-lg bg-primary-600 text-background-50 flex items-center justify-center text-xl">
              <i className="ri-movie-2-line" />
            </span>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-950 whitespace-nowrap">
              漂亮的保姆
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" className={linkClass} end>
              首页
            </NavLink>
            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`px-3 py-2 text-sm rounded-md transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap ${
                  catOpen ? "text-primary-600 font-semibold" : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                剧集分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-72 p-2 bg-background-50 border border-background-200 rounded-lg grid grid-cols-2 gap-1">
                  {categories.map((c) => (
                    <Link
                      key={c.type}
                      to={`/category/${c.type}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className={`${c.icon} text-base text-primary-500`} />
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            {navItems.slice(1).map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex ml-auto items-center gap-2">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索剧集、演员…"
                aria-label="搜索剧集或演员"
                className="w-52 lg:w-64 h-9 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="h-9 px-4 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="flex md:hidden items-center gap-1 ml-auto">
            <Link
              to="/search"
              aria-label="搜索"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:text-primary-600 cursor-pointer text-xl"
            >
              <i className="ri-search-line" />
            </Link>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMobileOpen((v) => !v)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 cursor-pointer text-2xl"
            >
              <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-fade-in">
          <form onSubmit={submitSearch} className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索剧集、演员…"
                aria-label="搜索剧集或演员"
                className="w-full h-10 pl-9 pr-3 text-sm rounded-md bg-background-100 border border-background-200 focus:border-primary-400 focus:outline-none text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="h-10 px-4 rounded-md bg-primary-600 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <nav className="flex flex-col gap-1">
            <NavLink to="/" className={linkClass} end>
              首页
            </NavLink>
            {navItems.slice(1).map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <p className="mt-4 mb-2 text-xs text-foreground-400">剧集分类</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((c) => (
              <Link
                key={c.type}
                to={`/category/${c.type}`}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 bg-background-100 cursor-pointer"
              >
                <i className={`${c.icon} text-base text-primary-500`} />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}